<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import ClientIntegrationSettings
        from '$lib/components/drawers/client/integrations/ClientIntegrationSettings.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import SyncSettings from '$lib/components/drawers/client/integrations/SyncSettings.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientId: number;
  export let clientKey: string | undefined;
  export let organisationId: string | undefined;

  type BusinessCentralIntegration = {
    authenticationType: string;
    canSync: boolean;
    canInvoicePost: boolean;
    canExpenseClaimPost: boolean;
    syncTaxCodes: boolean;
    syncSuppliers: boolean;
    syncGeneralLedgerCodes: boolean;
    syncBudgets: boolean;
    syncSpends: boolean;
    azureTenantId: string;
    businessCentralEnvironment: string;
    businessCentralCompanyId: string;
    clientDataProfile: string;
    businessCentralEndpointPublisher: string | null;
    businessCentralEndpointGroup: string | null;
    clientCredentialsClientId: string | null;
    hasClientCredentialsSecret: boolean;
    hasEntityConfig: boolean;
  };

  type EntityRow = {
    id: number;
    key: string;
    name: string;
    code: string;
    integration: BusinessCentralIntegration | null;
  };

  type Form = {
    azureTenantId: string;
    businessCentralEnvironment: string;
    businessCentralCompanyId: string;
    clientDataProfile: string;
    businessCentralEndpointPublisher: string;
    businessCentralEndpointGroup: string;
    clientCredentialsClientId: string;
    clientCredentialsClientSecret: string;
    syncSettings: Record<string, boolean>;
  };

  type ConnectForm = {
    azureTenantId: string;
    clientCredentialsClientId: string;
    clientCredentialsClientSecret: string;
  };

  const dataProfiles = ['BusinessCentral', 'DOBCEL'];
  let entities: EntityRow[] = [];
  let forms: Record<number, Form> = {};
  let connectForms: Record<number, ConnectForm> = {};
  let expandedId: number | null = null;
  let isLoading = false;
  let isRefreshing = false;
  let isForcingSync = false;
  let isConnecting: Record<number, boolean> = {};
  let isSaving: Record<number, boolean> = {};

  $: hasConfiguredEntities = entities.some((entity) => entity.integration);

  onMount(load);

  async function load(preserveScroll = false, showLoading = true) {
    if (isLoading || isRefreshing) return;
    if (showLoading) {
      isLoading = true;
    } else {
      isRefreshing = true;
    }
    const scrollY = preserveScroll ? window.scrollY : 0;
    try {
      const response = await fetchWithAuth(`/api/business-central/entities/${clientId}`);
      if (response.ok) {
        entities = await response.json();
      } else {
        showToast({ title: 'Error', message: 'Failed to load Business Central entities', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to load Business Central entities', type: 'error' });
    } finally {
      isLoading = false;
      isRefreshing = false;
      if (preserveScroll) requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
    }
  }

  function openRow(entity: EntityRow) {
    if (expandedId === entity.id) {
      expandedId = null;
      return;
    }

    expandedId = entity.id;
    const integration = entity.integration;
    if (!integration) {
      connectForms[entity.id] = {
        azureTenantId: '',
        clientCredentialsClientId: '',
        clientCredentialsClientSecret: '',
      };
      return;
    }

    forms[entity.id] = {
      azureTenantId: integration.azureTenantId,
      businessCentralEnvironment: integration.businessCentralEnvironment,
      businessCentralCompanyId: integration.businessCentralCompanyId,
      clientDataProfile: integration.clientDataProfile || 'BusinessCentral',
      businessCentralEndpointPublisher: integration.businessCentralEndpointPublisher ?? '',
      businessCentralEndpointGroup: integration.businessCentralEndpointGroup ?? '',
      clientCredentialsClientId: integration.clientCredentialsClientId ?? '',
      clientCredentialsClientSecret: '',
      syncSettings: {
        canSync: integration.canSync,
        canInvoicePost: integration.canInvoicePost,
        canExpenseClaimPost: integration.canExpenseClaimPost,
        syncTaxCodes: integration.syncTaxCodes,
        syncSuppliers: integration.syncSuppliers,
        syncGeneralLedgerCodes: integration.syncGeneralLedgerCodes,
        syncBudgets: integration.syncBudgets,
        syncSpends: integration.syncSpends,
      },
    };
  }

  async function connect(entity: EntityRow) {
    const form = connectForms[entity.id];
    if (!form || !clientKey) return;

    isConnecting = { ...isConnecting, [entity.id]: true };
    try {
      const response = await fetchWithAuth(`/api/business-central/clients/${clientId}/entities/${entity.id}/connect-with-client-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          azureTenantId: form.azureTenantId,
          clientCredentialsClientId: form.clientCredentialsClientId,
          clientCredentialsClientSecret: form.clientCredentialsClientSecret,
        }),
      });

      if (response.ok) {
        showToast({ title: 'Success', message: 'Business Central connected', type: 'success' });
        expandedId = null;
        await load();
      } else {
        showToast({ title: 'Error', message: 'Failed to connect Business Central', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to connect Business Central', type: 'error' });
    } finally {
      isConnecting = { ...isConnecting, [entity.id]: false };
    }
  }

  async function save(entity: EntityRow, isAutoSave = false) {
    const form = forms[entity.id];
    if (!form || !clientKey) return;

    isSaving = { ...isSaving, [entity.id]: true };
    try {
      const response = await fetchWithAuth(`/api/business-central/config/${clientId}/${entity.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          canSync: form.syncSettings.canSync,
          canInvoicePost: form.syncSettings.canInvoicePost,
          canExpenseClaimPost: form.syncSettings.canExpenseClaimPost,
          syncTaxCodes: form.syncSettings.syncTaxCodes,
          syncSuppliers: form.syncSettings.syncSuppliers,
          syncGeneralLedgerCodes: form.syncSettings.syncGeneralLedgerCodes,
          syncBudgets: form.syncSettings.syncBudgets,
          syncSpends: form.syncSettings.syncSpends,
          azureTenantId: form.azureTenantId,
          businessCentralEnvironment: form.businessCentralEnvironment,
          businessCentralCompanyId: form.businessCentralCompanyId,
          clientDataProfile: form.clientDataProfile,
          businessCentralEndpointPublisher: form.businessCentralEndpointPublisher || null,
          businessCentralEndpointGroup: form.businessCentralEndpointGroup || null,
          clientCredentialsClientId: form.clientCredentialsClientId || null,
          clientCredentialsClientSecret: form.clientCredentialsClientSecret || null,
        }),
      });

      if (response.ok) {
        showToast({ title: 'Success', message: isAutoSave ? 'Business Central sync settings saved' : 'Business Central configuration saved', type: 'success' });
        await load(true, false);
      } else {
        showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Business Central sync settings' : 'Failed to save Business Central configuration', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Business Central sync settings' : 'Failed to save Business Central configuration', type: 'error' });
    } finally {
      isSaving = { ...isSaving, [entity.id]: false };
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/business-central/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok
          ? { title: 'Success', message: 'Business Central sync has been queued', type: 'success' }
          : { title: 'Error', message: 'Failed to queue Business Central sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue Business Central sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-1 text-xl font-semibold">Business Central Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure each connected Business Central entity.</p>

  <ClientIntegrationSettings basePath="/api/business-central" {clientId} />
  <div class="mb-4 flex justify-end gap-2">
    <Button label={isForcingSync ? 'Queueing...' : 'Force sync'} onClick={forceSync} disabled={isLoading || isForcingSync || !hasConfiguredEntities} size="small" />
    <Button label={isLoading ? 'Loading...' : 'Refresh'} onClick={load} disabled={isLoading || isForcingSync} colour="secondary" size="small" />
  </div>

  <h4 class="mb-3 text-lg font-semibold text-gray-800">Entities</h4>
  {#if isLoading}
    <p class="text-sm text-gray-500">Loading entities...</p>
  {:else if entities.length === 0}
    <p class="text-sm text-gray-500">No entities found for this client.</p>
  {:else}
    <div class="space-y-2">
      {#each entities as entity (entity.id)}
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          <button type="button" class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100" on:click={() => openRow(entity)}>
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">{entity.name}</span>
              {#if entity.integration}
                <span class="text-xs text-gray-500">{entity.integration.authenticationType}</span>
                <span class="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Configured</span>
              {:else}
                <span class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">Not configured</span>
              {/if}
            </div>
            <span class="ml-2 text-sm text-gray-400">{expandedId === entity.id ? '▲' : '▼'}</span>
          </button>

          {#if expandedId === entity.id}
            <div class="border-t border-gray-200 px-4 pb-4 pt-4">
              {#if !entity.integration}
                <div class="space-y-4">
                  <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Connect with Client Credentials</h5>
                  <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <label for="bc-connect-tenant-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Azure Tenant ID</label>
                      <FieldHint text="The Microsoft Entra tenant that owns this Business Central environment." />
                      <input
                        id="bc-connect-tenant-{entity.id}"
                        bind:value={connectForms[entity.id].azureTenantId}
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        disabled={isConnecting[entity.id]}
                      />
                    </div>
                    <div>
                      <label for="bc-connect-client-id-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Client ID</label>
                      <FieldHint text="The application ID for the Business Central service principal." />
                      <input
                        id="bc-connect-client-id-{entity.id}"
                        bind:value={connectForms[entity.id].clientCredentialsClientId}
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        disabled={isConnecting[entity.id]}
                      />
                    </div>
                    <div>
                      <label for="bc-connect-client-secret-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Client Secret</label>
                      <FieldHint text="The service principal secret used to authenticate this connection." />
                      <input
                        id="bc-connect-client-secret-{entity.id}"
                        type="password"
                        bind:value={connectForms[entity.id].clientCredentialsClientSecret}
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        disabled={isConnecting[entity.id]}
                      />
                    </div>
                  </div>
                  <div class="flex justify-end">
                    <Button
                      label={isConnecting[entity.id] ? 'Connecting...' : 'Connect Business Central'}
                      onClick={() => connect(entity)}
                      disabled={isConnecting[entity.id] ||
                        !clientKey ||
                        !connectForms[entity.id].azureTenantId ||
                        !connectForms[entity.id].clientCredentialsClientId ||
                        !connectForms[entity.id].clientCredentialsClientSecret}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>
              {:else if forms[entity.id]}
                <div class="space-y-6">
                  <SyncSettings settings={forms[entity.id].syncSettings} disabled={isSaving[entity.id]} isSaving={isSaving[entity.id]} onChange={() => save(entity, true)} />

                  <div>
                    <h5 class="mb-2 border-b pb-2 text-sm font-semibold text-gray-700">Connection Settings</h5>
                    <p class="text-sm text-gray-600">Authentication type: {entity.integration.authenticationType}</p>
                  </div>

                  <div class="space-y-4">
                    <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Business Central Settings</h5>
                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="bc-tenant-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Azure Tenant ID</label>
                        <FieldHint text="The Microsoft Entra tenant that owns this Business Central environment." />
                        <input
                          id="bc-tenant-{entity.id}"
                          bind:value={forms[entity.id].azureTenantId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="bc-environment-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Environment</label>
                        <FieldHint text="The Business Central environment name, such as Production or Sandbox." />
                        <input
                          id="bc-environment-{entity.id}"
                          bind:value={forms[entity.id].businessCentralEnvironment}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="bc-company-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Company ID</label>
                        <FieldHint text="The Business Central company GUID to use for this entity." />
                        <input
                          id="bc-company-{entity.id}"
                          bind:value={forms[entity.id].businessCentralCompanyId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="bc-profile-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Client Data Profile</label>
                        <FieldHint text="Selects the Business Central data model used for synchronisation." />
                        <select
                          id="bc-profile-{entity.id}"
                          bind:value={forms[entity.id].clientDataProfile}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                          >{#each dataProfiles as profile}<option value={profile}>{profile}</option>{/each}</select
                        >
                      </div>
                      <div>
                        <label for="bc-publisher-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Endpoint Publisher</label>
                        <FieldHint text="Optional publisher name for custom Business Central APIs." />
                        <input
                          id="bc-publisher-{entity.id}"
                          bind:value={forms[entity.id].businessCentralEndpointPublisher}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="bc-group-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Endpoint Group</label>
                        <FieldHint text="Optional API group for custom Business Central endpoints." />
                        <input
                          id="bc-group-{entity.id}"
                          bind:value={forms[entity.id].businessCentralEndpointGroup}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label for="bc-client-id-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Client Credentials Client ID</label>
                        <FieldHint text="The application ID used to renew this entity's access token." />
                        <input
                          id="bc-client-id-{entity.id}"
                          bind:value={forms[entity.id].clientCredentialsClientId}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="bc-client-secret-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Client Credentials Secret</label>
                        <FieldHint text="Leave blank to keep the existing secret unchanged." />
                        <input
                          id="bc-client-secret-{entity.id}"
                          type="password"
                          bind:value={forms[entity.id].clientCredentialsClientSecret}
                          placeholder={entity.integration.hasClientCredentialsSecret ? 'Configured; leave blank to keep' : 'Not configured'}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                          disabled={isSaving[entity.id]}
                        />
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-end">
                    <Button
                      label={isSaving[entity.id] ? 'Saving...' : 'Save'}
                      onClick={() => save(entity)}
                      disabled={!!isSaving[entity.id] || !clientKey}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
